import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { QuizQuestion } from '@/mocks/lessons';

interface QuizMeProps {
  questions: QuizQuestion[];
}

export default function QuizMe({ questions }: QuizMeProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState<boolean[]>(new Array(questions.length).fill(false));
  const [results, setResults] = useState<Array<{ correct: boolean; q: number }>>([]);

  const question = questions[currentQ];
  const isCorrect = selected === question.correctIndex;
  const allAnswered = answered.every((a) => a);

  const handleSelect = (idx: number) => {
    if (showResult) return;
    setSelected(idx);
    setShowResult(true);
    const newAnswered = [...answered];
    newAnswered[currentQ] = true;
    setAnswered(newAnswered);
    const correct = idx === question.correctIndex;
    if (correct) setScore((s) => s + 1);
    setResults((prev) => [...prev, { correct, q: currentQ }]);
  };

  const handleNext = () => {
    if (currentQ < questions.length - 1) {
      setCurrentQ((q) => q + 1);
      setSelected(null);
      setShowResult(false);
    }
  };

  const handleRestart = () => {
    setCurrentQ(0);
    setSelected(null);
    setShowResult(false);
    setScore(0);
    setAnswered(new Array(questions.length).fill(false));
    setResults([]);
  };

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    textSuccess: isDark ? 'text-emerald-400' : 'text-emerald-600',
    textError: isDark ? 'text-red-400' : 'text-red-600',
    textAnswer: isDark ? 'text-white/40' : 'text-black/40',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    progressDone: isDark ? 'bg-emerald-500/60' : 'bg-emerald-500',
    progressWrong: isDark ? 'bg-red-500/60' : 'bg-red-500',
    progressCurrent: isDark ? 'bg-white' : 'bg-black',
    progressEmpty: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]',
    optionCorrect: isDark ? 'bg-emerald-500/8 border-emerald-500/15 text-emerald-300' : 'bg-emerald-50 border-emerald-200/50 text-emerald-600',
    optionWrong: isDark ? 'bg-red-500/8 border-red-500/15 text-red-300' : 'bg-red-50 border-red-200/50 text-red-600',
    optionSelected: isDark ? 'bg-white/[0.06] border-white/10 text-white' : 'bg-black/[0.06] border-black/10 text-black',
    optionDefault: isDark ? 'bg-white/[0.02] border-white/[0.04] text-white/60 hover:bg-white/[0.04]' : 'bg-black/[0.02] border-black/[0.04] text-black/60 hover:bg-black/[0.04]',
    letterCorrect: isDark ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border-emerald-200/50 bg-emerald-50 text-emerald-600',
    letterWrong: isDark ? 'border-red-500/20 bg-red-500/10 text-red-300' : 'border-red-200/50 bg-red-50 text-red-600',
    letterSelected: isDark ? 'border-white/15 bg-white/[0.06] text-white/70' : 'border-black/15 bg-black/[0.06] text-black/70',
    letterDefault: isDark ? 'border-white/8 text-white/30' : 'border-black/8 text-black/30',
    resultCard: isDark ? 'bg-white/[0.03] border-white/[0.05]' : 'bg-black/[0.03] border-black/[0.05]',
    btnPrimary: isDark ? 'bg-white text-black' : 'bg-black text-white',
    btnGhost: isDark ? 'bg-white/[0.04] text-white/50 border-white/[0.06]' : 'bg-black/[0.04] text-black/50 border-black/[0.06]',
    trophyBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    trophyIcon: isDark ? 'text-white' : 'text-black',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-questionnaire-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Quiz Me</h3>
          <p className={`${t.textFaint} text-[11px]`}>
            {allAnswered ? `${score} of ${questions.length} correct` : `Question ${currentQ + 1} of ${questions.length}`}
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-5">
        {questions.map((_, i) => (
          <div
            key={i}
            className={`h-[2px] flex-1 rounded-full transition-colors duration-300 ${
              i < currentQ
                ? results.find((r) => r.q === i)?.correct
                  ? t.progressDone
                  : t.progressWrong
                : i === currentQ
                  ? t.progressCurrent
                  : t.progressEmpty
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {allAnswered ? (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center text-center pt-4"
          >
            <motion.div
              className={`w-16 h-16 rounded-full ${t.trophyBg} flex items-center justify-center mb-5`}
              initial={{ scale: 0.6 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 350, damping: 20 }}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`ri-trophy-line ${t.trophyIcon} text-xl`} />
              </div>
            </motion.div>
            <h3 className={`${t.textPrimary} text-base font-bold mb-1`}>Quiz Complete</h3>
            <p className={`${t.textMuted} text-sm mb-6`}>
              You scored {score} out of {questions.length}
            </p>
            <div className="w-full max-w-xs space-y-2.5">
              <motion.button
                onClick={handleRestart}
                whileTap={{ scale: 0.95 }}
                className={`w-full ${t.btnPrimary} py-3 rounded-xl text-xs font-semibold cursor-pointer`}
              >
                Try Again
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                className={`w-full ${t.btnGhost} py-3 rounded-xl text-xs font-semibold cursor-pointer border`}
              >
                Back to Lesson
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={question.id}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          >
            <h3 className={`${t.textPrimary} text-sm font-semibold leading-relaxed mb-5`}>
              {question.question}
            </h3>
            <div className="space-y-2.5">
              {question.options.map((opt, idx) => {
                const isSelected = selected === idx;
                const isAnswerCorrect = idx === question.correctIndex;
                const showCorrect = showResult && isAnswerCorrect;
                const showWrong = showResult && isSelected && !isAnswerCorrect;

                return (
                  <motion.button
                    key={idx}
                    onClick={() => handleSelect(idx)}
                    whileTap={!showResult ? { scale: 0.97 } : {}}
                    className={`w-full text-left px-4 py-3.5 rounded-xl text-sm transition-all duration-200 cursor-pointer border ${
                      showCorrect
                        ? t.optionCorrect
                        : showWrong
                          ? t.optionWrong
                          : isSelected
                            ? t.optionSelected
                            : t.optionDefault
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${
                          showCorrect
                            ? t.letterCorrect
                            : showWrong
                              ? t.letterWrong
                              : isSelected
                                ? t.letterSelected
                                : t.letterDefault
                        }`}
                      >
                        {String.fromCharCode(65 + idx)}
                      </div>
                      <span className="text-xs">{opt}</span>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence>
              {showResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                  className={`mt-4 p-4 rounded-xl ${t.resultCard} border`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${isCorrect ? `ri-check-line ${t.textSuccess}` : `ri-close-line ${t.textError}`} text-xs`} />
                    </div>
                    <span className={`text-xs font-semibold ${isCorrect ? t.textSuccess : t.textError}`}>
                      {isCorrect ? 'Correct!' : 'Not quite'}
                    </span>
                  </div>
                  <p className={`${t.textAnswer} text-xs leading-relaxed`}>{question.explanation}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showResult && currentQ < questions.length - 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="mt-4"
                >
                  <motion.button
                    onClick={handleNext}
                    whileTap={{ scale: 0.95 }}
                    className={`w-full ${t.btnPrimary} py-3 rounded-xl text-xs font-semibold cursor-pointer`}
                  >
                    Next Question
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}